import { isEqual } from 'lodash';
import { createSelector } from 'reselect';
import { State } from 'store/types';

import { getPlanFloorOrder } from 'lib/riskbase';
import { PinNew, PinNewWithName, Plan, PlanLoaded } from './types';

type PlanState = Pick<State, 'plan'>;

const selectPlanState = (state: PlanState): PlanState['plan'] => state.plan;

const selectData = createSelector(selectPlanState, (plan) => plan.data!);

export const selectPlansContext = createSelector(
  selectData,
  (data) => data.context,
);

export const selectPlansAssetId = createSelector(
  selectPlansContext,
  (context): string => context.assetId || '',
);

export const selectSelectionKey = createSelector(
  selectData,
  (data): string | undefined => data.selectionKey,
);

export const selectPlansEditable = createSelector(
  selectData,
  (data): boolean => !!data.plansEditable,
);

export const selectPlansFindNoun = createSelector(
  selectData,
  (data) => data.findNoun,
);

export const selectAfterActionFind = createSelector(
  selectData,
  (data) => data.actionAfterFind,
);

export const selectPlans = createSelector(
  selectData,
  (data): Record<string, Plan> => data!.plans,
);

export const selectPlansWithFloorOrder = createSelector(
  selectData,
  (data): Record<string, Plan & { floorOrder: number }> =>
    Object.fromEntries(
      Object.entries(data!.plans || {}).map<
        [string, Plan & { floorOrder: number }]
      >(([i, plan]) => [
        i,
        {
          fileId: plan.fileId,
          id: plan.id,
          name: plan.name,
          pinCount: plan.pinCount,
          filePage: plan.filePage,
          fileType: plan.fileType,
          fileWidth: plan.fileWidth || 0,
          fileHeight: plan.fileHeight || 0,
          floorOrder: getPlanFloorOrder(plan.name),
        },
      ]),
    ),
);

export const selectPlansPlan = createSelector(
  selectData,
  (data): PlanLoaded | undefined => data.plan,
);

export const selectPlansPins = createSelector(
  selectData,
  (data): PinNewWithName[] => data.pins,
);

export const selectPlansPlanPins = createSelector(
  selectData,
  (data): PinNewWithName[] => {
    const planId = data.plan?.id;
    if (!planId) {
      return [];
    }
    return data!.pins.filter((pin) => pin.planId === planId);
  },
);

export const selectPlansPinInCurrentPlan = createSelector(
  selectData,
  (data): PinNew | undefined =>
    data.pin?.planId === data.plan?.id ? data.pin : undefined,
);

export const selectPlansPin = createSelector(
  selectData,
  (data): PinNew | undefined => data.pin,
);

const selectPlansInitPin = createSelector(
  selectData,
  (data): PinNew | undefined => data.initPin,
);

export const selectPlansPinIsDirty = createSelector(
  selectPlansPin,
  selectPlansInitPin,
  (pin, initPin): boolean => !isEqual(pin, initPin),
);

export const selectPlansSelectedPinId = createSelector(
  selectData,
  (data): string | undefined => data.selectedPinId,
);

export const selectPlansPinPlanId = createSelector(
  selectPlansPin,
  (pin): string | undefined => pin?.planId,
);

export const selectPlansPlanId = createSelector(
  selectPlansPlan,
  (plan): string | undefined => plan?.id,
);
